import { reactive, computed, watch, type WatchStopHandle } from 'vue'
import {
  enableUserLayersForTags,
  layerManagementReady,
  toggleUserLayerActive,
} from '@/services/layer-management-service'
import { useLayers } from '@/services/layer-management-service/composables/use-layers'
import type { UserLayer } from '@onxmaps/layer-management'
import { camera, getReady, viewerDimension } from '@/services/map-service'
import marketingEventService from '@/services/marketing-event-service'

export enum Basemap {
  TOPO = 'topo',
  HYBRID = 'hybrid',
  AERIAL = 'aerial',
}

export enum BasemapImagery {
  LEAF_OFF = 'aerial-leaf-off',
  HIGH_FREQ = 'aerial-high-frequency',
  WINTER_AERIAL = 'aerial-winter',
  DYNAMIC_HILLSHADE = 'dynamic-hillshade',
}

const { getUserLayerById } = useLayers()

// exposing for backwards compatability
export const TOPO = Basemap.TOPO
export const HYBRID = Basemap.HYBRID
export const AERIAL = Basemap.AERIAL

interface State {
  basemap: string
}

const observableState = reactive<State>({
  basemap: Basemap.AERIAL,
})

// Updating basemap state based on LMS

const basemapUpdate = (layer: UserLayer | undefined) => {
  if (layer?.active) {
    observableState.basemap = layer.id
  }
}

watch(
  computed(() => getUserLayerById(TOPO)),
  basemapUpdate
)

watch(
  computed(() => getUserLayerById(HYBRID)),
  basemapUpdate
)

watch(
  computed(() => getUserLayerById(AERIAL)),
  basemapUpdate
)

const leafOff = computed(() => {
  return getUserLayerById(BasemapImagery.LEAF_OFF)
})

const winterAerial = computed(() => {
  return getUserLayerById(BasemapImagery.WINTER_AERIAL)
})

const highFreq = computed(() => {
  return getUserLayerById(BasemapImagery.HIGH_FREQ)
})

const dynamicHillshade = computed(() => {
  return getUserLayerById(BasemapImagery.DYNAMIC_HILLSHADE)
})

/**
 * *(AI-generated)*
 * Returns whether the leaf-off imagery layer is active.
 */
export const isLeafOffActive = () => {
  return leafOff.value?.isActive
}

/**
 * *(AI-generated)*
 * Returns whether the winter aerial imagery layer is active.
 */
export const isWinterAerialActive = () => {
  return winterAerial.value?.isActive
}

/**
 * *(AI-generated)*
 * Returns whether the high-frequency imagery layer is active.
 */
export const isHighFreqActive = () => {
  return highFreq.value?.isActive
}

/**
 * *(AI-generated)*
 * Returns whether the dynamic hillshade layer is active.
 */
export const isDynamicHillshadeActive = () => {
  return dynamicHillshade.value?.isActive
}

export const basemapMarketingName = computed(() => {
  const leafOff = isLeafOffActive() ? ' leaf off' : ''
  const winterAerial = isWinterAerialActive() ? ' winter' : ''
  const highFreq = isHighFreqActive() ? ' high frequency' : ''
  const dynamicHillshade = isDynamicHillshadeActive()
    ? ' dynamic hillshade'
    : ''

  const baseName = basemap() === 'aerial' ? 'satellite' : basemap()

  return baseName + winterAerial + leafOff + highFreq + dynamicHillshade
})

/**
 * *(AI-generated)*
 * Initializes the basemap service by setting up mode watchers and applying saved basemap settings.
 * Note: this restores saved user preferences via LMS. For new users with no saved preference,
 * call ensureDefaultBasemap() AFTER the map is fully ready (mapViewerReady) to set the default.
 */
export const initializeBasemap = async () => {
  initModeWatchers()

  await layerManagementReady()

  // Turn on basemap tags (restores saved user preferences or stylesheet defaults)
  enableUserLayersForTags(['basemaps', 'basemap-toggles'], true)
}

/**
 * Ensures a default basemap (AERIAL) is active if no basemap preference has been set.
 * Must be called AFTER the map is fully initialized (mapViewerReady / map-x-ready event),
 * otherwise the setBasemap call fires before Mapbox GL exists and has no visual effect.
 *
 * Safe to call for existing users — if any basemap is already active, this is a no-op.
 */
export const ensureDefaultBasemap = () => {
  const isAnyBasemapActive = [
    Basemap.TOPO,
    Basemap.HYBRID,
    Basemap.AERIAL,
  ].some((id) => getUserLayerById(id)?.active)
  if (!isAnyBasemapActive) {
    setBasemap(Basemap.AERIAL)
  }
}

/**
 * Return current basemap name
 */
export const basemap = () => observableState.basemap

/**
 * Sets new basemap name and update visibility of each provided basemap layer
 */
export const setBasemap = (userLayerId: string) => {
  toggleUserLayerActive(userLayerId, true)
}

/**
 * Is the current basemap topo.
 */
export const isBasemapTopo = () => basemap() === TOPO

const watchers: WatchStopHandle[] = []

/**
 * Create watchers to update basemap state when activity modes change
 */
const initModeWatchers = () => {
  while (watchers.length) {
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    watchers.shift()!.call(this)
  }

  // fish-web has no activity mode concept (no offroad/backcountry modes)
}

/**
 * Send marketing event when basemap changes
 */
export const triggerBasemapSelectedEvent = () => {
  if (getReady() !== true || !camera()) {
    return
  }

  // Because the basemap marketing value is updated via watchers, we need to let
  // any potential updates complete before publishing this event.
  setTimeout(() => {
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const { location } = camera()!
    // `active_basemap` prop should already be set on
    marketingEventService.publish('basemap_selected', {
      // `active_basemap` prop should already be set before this is triggered
      new_basemap: basemapMarketingName.value,
      map_viewer_dimension: viewerDimension(),
      location_mode: 'fixed location',
      lat_long: location.toDegreesString(),
      map_mode: 'fishing',
    })
  })
}

export const basemapDisplayNames: Record<BasemapImagery, string> = {
  [BasemapImagery.DYNAMIC_HILLSHADE]: 'Lidar',
  [BasemapImagery.LEAF_OFF]: 'Leaf-Off Imagery',
  [BasemapImagery.HIGH_FREQ]: 'Recent Imagery',
  [BasemapImagery.WINTER_AERIAL]: 'Snow Imagery',
} as const
